import React, { useMemo, useState, useEffect } from 'react';
import {
  Checkbox,
  VStack,
  Flex,
  Text,
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  Link,
} from '@chakra-ui/react';
import { useMerchantGroup, useMerchantTheme } from '@domain/merchants/queries';
import { createLocationString } from '@domain/merchants/functions/createLocationString';
import { createMerchantPath } from '@domain/merchants/hooks/useMerchantPath';
import { useUpdateMerchantTheme } from '@domain/merchants/mutations/useUpdateMerchantTheme';
import isEqual from 'lodash/isEqual';
import { useError, useToast } from '@olo-web/utils/common/hooks';

const MerchantLocationCheckbox = ({ merchant, theme, isChecked, onChange }) => {
  const { data: specificTheme, isLoading } = useMerchantTheme(
    theme?.merchantIds?.includes(merchant?.merchantId) ? null : merchant?.merchantId
  );

  const merchantPath = useMemo(() => createMerchantPath({ merchant }), [merchant]);

  const alreadyHasDifferentTheme = useMemo(() => {
    return !!specificTheme && specificTheme.id !== theme?.id;
  }, [specificTheme, theme?.id]);

  const handleChange = () => {
    onChange(merchant?.merchantId);
  };

  return (
    <Checkbox
      size="lg"
      value={merchant.merchantantId}
      isDisabled={isLoading || alreadyHasDifferentTheme}
      isChecked={isChecked}
      onChange={handleChange}
    >
      <Flex flexDir="column" ml={4}>
        <Text textStyle="h3">{merchant?.name}</Text>
        <Text textStyle="subtitle1" fontWeight="400">
          {createLocationString(merchant)}
        </Text>
        {alreadyHasDifferentTheme && (
          <Popover>
            <PopoverTrigger>
              <Button variant="link" colorScheme="primary" alignSelf="flex-start" mt={2}>
                Why is this disabled?
              </Button>
            </PopoverTrigger>
            <PopoverContent>
              <PopoverArrow />
              <PopoverBody>
                <Text textStyle="p" fontSize="md">
                  This location is already assigned to another theme.{' '}
                  <Link
                    isExternal
                    textDecor="underline"
                    color="primary.350"
                    href={merchantPath + '?updateTheme=true'}
                  >
                    Go to this location url
                  </Link>{' '}
                  to view its assigned theme.
                </Text>
              </PopoverBody>
            </PopoverContent>
          </Popover>
        )}
      </Flex>
    </Checkbox>
  );
};

export const ThemeMerchantLocations = () => {
  const { data: group } = useMerchantGroup();
  const { data: theme } = useMerchantTheme();
  const callError = useError();
  const { notify } = useToast();
  const { mutate, isLoading } = useUpdateMerchantTheme({
    onSuccess: (_, variables) => {
      const isDelete = !variables.theme.merchantIds?.length;
      const title = isDelete ? 'Theme deleted' : 'Locations updated';
      notify({
        status: isDelete ? 'warning' : 'success',
        title,
        description: 'Your changes are now live',
        variant: 'left-accent',
        position: 'bottom-left',
      });
    },
    onError: (error) => callError({ error, toastError: true }),
  });
  const [merchantIds, setMerchantIds] = useState<string[]>([]);

  useEffect(() => {
    setMerchantIds(theme?.merchantIds ?? []);
  }, [theme?.merchantIds]);

  const showSaveButton = useMemo(() => {
    if (!merchantIds?.length) return false;
    if (!theme?.merchantIds?.length) return true;
    return !isEqual(merchantIds.sort(), theme.merchantIds.sort());
  }, [merchantIds, theme?.merchantIds]);

  const showDeleteButton = useMemo(() => {
    return !merchantIds?.length && theme?.merchantIds?.length;
  }, [merchantIds, theme?.merchantIds]);

  const handleUpdate = () => {
    mutate({
      docId: theme?.id ? theme?.id : '',
      theme: { merchantIds },
    });
  };

  const handleDelete = () => {
    mutate({
      docId: theme?.id ? theme?.id : '',
      theme: { merchantIds: [] },
    });
  };

  const handleChange = (id: string) => {
    const idx = merchantIds.indexOf(id);
    if (idx >= 0) {
      setMerchantIds((prev) => [...prev.slice(0, idx), ...prev.slice(idx + 1)]);
      return;
    }
    setMerchantIds((prev) => [...prev, id]);
  };

  return (
    <VStack w="100%" spacing={10} align="flex-start">
      <Text>Update the locations that are using this theme</Text>
      <VStack w="100%" spacing={8} align="flex-start">
        {group?.merchants.map((merch) => (
          <MerchantLocationCheckbox
            merchant={merch}
            theme={theme}
            key={merch.merchantId}
            onChange={handleChange}
            isChecked={merchantIds.includes(merch.merchantId)}
          />
        ))}
      </VStack>
      {showSaveButton && (
        <Button
          w="100%"
          colorScheme="primary"
          size="lg"
          onClick={handleUpdate}
          isLoading={isLoading}
        >
          Update locations using this theme
        </Button>
      )}
      {showDeleteButton && (
        <Button
          w="100%"
          colorScheme="danger"
          variant="outline"
          size="lg"
          onClick={handleDelete}
          isLoading={isLoading}
        >
          Delete this theme
        </Button>
      )}
    </VStack>
  );
};
